import {memo, useCallback, useEffect, useState} from 'react';

import styled from 'styled-components';
import {useSelector} from 'react-redux';

import {api} from '~/shared/services/bffService';
import {getLocalizationService} from '~/shared/services/localisationService';
import {CloseButton as _CloseButton, ModalContent as _ModalContent} from '~/shared/components/Modals';
import {media} from '~/shared/theme/media';
import {body16Bold, body16Normal, body18Bold, subheader24Bold} from '~/shared/theme/typography';
import {flexCenterVertically, FlexColumn, flexColumnCenter} from '~/shared/theme/FlexLayout';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import closedDialogButtonUrl from '~/assets/images/dialogs/closeDialogButton.svg';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import {getUserSessionToken, setUserSessionToken} from '~/shared/utils/userToken';
import VerificationForm from '~/shared/components/VerificationForm';
import {selectUserData} from '~/shared/store/selectors';
import apiService from '~/shared/services/apiService';
import {CentralLoading} from '~/shared/components/Loaders';
import {trackEvent} from '~/shared/services/analytics';
import {createLogger} from '~/shared/logging';
import {VerificationFormContextProvider} from '~/common/contextProviders/VerificationFormContextProvider';
import {handleRefreshToken, logout} from '~/shared/services/auth';
import {is401Error} from '~/shared/services/apiErrorService';
import PieButton from '~/shared/theme/pie/PieButton';
import errorImageUrl from '~/shared/components/ModalErrorView/errorTriangle.png';
import {capitalFirstLetterAfterSpace} from '~/shared/utils/general';

import accountDeletedImageUrl from './assets/goodbye-large.png';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;

  && {
    width: 328px;
    ${media.minLargeMobile`
      width: 500px;
    `}
    background-color: ${({theme}) => theme.colors.surface};
  }
`;

const ModalContent = styled(_ModalContent)`
  width: auto;
  margin: 0;
  padding: 20px;
  ${media.minLargeMobile`
    padding: 32px;
  `}
  ${flipOnLTR`
    text-align: right;
  `}
`;

const DialogHeader = styled.div<{withShadow: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  height: 64px;
  box-shadow: ${({withShadow, theme}) => (withShadow ? theme.shadows.modalHeaderShadow : 'none')};
`;

const DialogHeaderText = styled.div`
  ${body18Bold};
  color: ${({theme}) => theme.colors.charcoal80};
`;

const CloseButton = styled(_CloseButton)`
  top: 16px;
  ${flipOnLTR`
    left: 16px; 
  `}
`;

const ClosePopupButtonSvg = styled(ImageWithAlt).attrs({
  src: closedDialogButtonUrl,
})``;

const InformationPointsSection = styled(FlexColumn)`
  align-items: flex-start;
`;

const InfoRow = styled.div`
  ${body16Normal}
  line-height: 24px;
  display: flex;

  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const InfoText = styled.span``;

const InfoRowIndex = styled.div`
  display: flex;
  justify-content: center;
  width: 16px;
  text-align: center;
  ${flipOnLTR`
    margin-left: 6px;
  `}
`;

const ButtonsWrapper = styled.div`
  ${flexCenterVertically}
  flex-direction: column-reverse;
  padding: 0 20px 20px 20px;
  width: 100%;
  ${media.minLargeMobile`
    flex-direction: row;
    padding: 0 32px 24px 32px;
    justify-content: space-between;
  `}
`;

const CommonButton = styled(PieButton)<{fullWidth?: boolean}>`
  width: 100%;
  ${media.minLargeMobile`
    width: ${({fullWidth}) => (fullWidth ? '100%' : '210px')};
  `}
`;

const CancelButton = styled(CommonButton)`
  margin-top: 16px;
  ${media.minLargeMobile`
    margin-top: 0;
    ${flipOnLTR`
      margin-left: 16px;
    `}  
  `}
`;

const ContinueButton = styled(CommonButton)<{fullWidth?: boolean}>`
  ${({fullWidth}) => fullWidth && 'width: 100%;'}
`;

const ResponseContent = styled.div`
  ${flexColumnCenter}
  text-align: center;
  margin-top: 12px;
  ${media.minLargeMobile`
    margin-top: 0;
  `}
`;

const DeletedAccountImage = styled(ImageWithAlt).attrs({
  noAlt: true,
})`
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  width: 200px;
  height: 200px;
`;

const logger = createLogger('Delete account');

const informationPoints = [
  'delete_account_info_point_1',
  'delete_account_info_point_2',
  'delete_account_info_point_3',
  'delete_account_info_point_4',
];

const UserDeletedTitle = styled.div`
  ${subheader24Bold}
  line-height: 32px;
  margin-top: 32px;
`;

const UserDeletedText = styled.div`
  ${body16Normal}
  margin-top: 8px;
`;

const StyledVerificationForm = styled(VerificationForm)`
  .resend-code-button {
    color: ${({theme}) => theme.colors.contentDefault};
    ${body16Bold}
  }

  .confirm-button {
    ${body16Bold}
    color: ${({theme}) => theme.colors.contentWhite};
    background-color: ${({theme}) => theme.colors.actionBlack};
    border-radius: 100px;

    :hover {
      background-color: ${({theme}) => theme.colors.actionBlackHover};
    }
  }

  .form-input {
    border-radius: 12px;
    border: 1px solid ${({theme}) => theme.colors.borderStrong};
  }

  .verification-form-header {
    ${body16Normal}
    color: ${({theme}) => theme.colors.contentDefault};
  }
 
  .verification-form-email {
    ${body16Bold}
    color: ${({theme}) => theme.colors.contentDefault};
  }
`;

type DeleteAccountWarningProps = {
  onCancel: () => void;
  onContinue?: () => void;
};

const DeleteAccountWarning = ({onContinue, onCancel}: DeleteAccountWarningProps) => {
  const userData = useSelector(selectUserData);
  const [verificationFormErrors, setVerificationFormErrors] = useState<string | null>(null);
  const [apiResponse, setApiResponse] = useState<string | undefined>();
  const [isVerified, setIsVerified] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [verificationData, setVerificationData] = useState<Record<string, any>>();
  const [firstConfirmation, setFirstConfirmation] = useState<boolean>(false);

  const identificationProcess = verificationData?.isPhoneMethod || userData?.cellphone ? 1 : 0;

  const {t} = getLocalizationService();

  const getUserAuthCode = useCallback(async () => {
    try {
      const {data}: Record<string, any> = await apiService.getUserAuthenticationDataAndSendAuthenticationCodeToUser({
        email: userData?.email,
      });

      const {codeAuthenticationData} = data;

      setVerificationData({
        authenticationToken: codeAuthenticationData?.authenticationToken,
        isPhoneMethod: codeAuthenticationData?.sendingMethod === 'Phone',
        lastFourPhoneDigits: codeAuthenticationData?.lastFourPhoneDigits,
      });
    } catch (error) {
      const errorType = 'get user authentication code failed';
      logger.error(errorType, {error, fingerprint: errorType});
    }
  }, [userData?.email]);

  useEffect(() => {
    if (!isVerified) {
      getUserAuthCode();
    }
  }, [isVerified, getUserAuthCode]);

  const handleRemoveAccount = async () => {
    try {
      setIsLoading(true);
      const token = await getUserSessionToken();

      if (!token) {
        setIsLoading(false);
        setIsVerified(false);
        return;
      }

      await api.removeUserAccount({
        headers: {
          'session-token': token,
        },
      });

      setApiResponse('success');
      setIsLoading(false);
      trackEvent('hasSuccessfullyDeleteAccount', {identificationProcess});
    } catch (error: any) {
      if (is401Error(error)) {
        await handleRefreshToken(error, handleRemoveAccount);
        return;
      }

      setApiResponse('failed');
      setIsLoading(false);
      trackEvent('hasFailedDeleteAccount', {identificationProcess});
      const errorType = 'delete account failed';
      setVerificationFormErrors(error?.message);
      logger.error(errorType, {error, fingerprint: errorType});
    }
  };

  const onSubmitVerification = async (entries: Record<string, any>) => {
    const {authenticationCode} = entries;

    if (!authenticationCode) {
      setVerificationFormErrors('onSubmitVerification didnt receive an authenticationCode');
      return;
    }

    setVerificationFormErrors(null);

    try {
      const params = {
        email: userData?.email,
        authenticationToken: verificationData?.authenticationToken,
        authenticationCode,
      };
      const {data: user} = await apiService.getUserV2(params);
      trackEvent('hasApproveDeleteAccount', {identificationProcess});
      setUserSessionToken(user?.sessionToken);
      setIsVerified(Boolean(user?.sessionToken));
      handleRemoveAccount();
    } catch (error: any) {
      setApiResponse('failed');
      setVerificationFormErrors(error?.message);
      const errorType = 'user verification failed';
      logger.error(errorType, {error, fingerprint: errorType});
    }
  };

  const DeleteAccountContent = () => {
    if (!isVerified) {
      const headerText = userData?.cellphone
        ? t('we_sent_you_an_sms_with_code_to_this_number_xxx', {
          phoneNumber: userData?.cellphone,
        })
        : t('we_sent_you_an_email_with_code_to_verify');
      return (
        <VerificationFormContextProvider>
          <StyledVerificationForm
            onReSendCode={getUserAuthCode}
            onSubmit={onSubmitVerification}
            email={userData?.email}
            headerText={headerText}
            setResErrors={setVerificationFormErrors}
            resErrors={verificationFormErrors}
          />
        </VerificationFormContextProvider>
      );
    }

    if (apiResponse) {
      return (
        <ResponseContent>
          <DeletedAccountImage src={apiResponse === 'success' ? accountDeletedImageUrl : errorImageUrl} />
          <UserDeletedTitle>{apiResponse === 'success' ? t('account_has_been_deleted') : t('your_account_not_deleted')}</UserDeletedTitle>
          <UserDeletedText>{apiResponse === 'success' ? t('we_hope_to_see_you_again_in_the_future') : t('delete_account_error_info')}</UserDeletedText>
        </ResponseContent>
      );
    }

    if (firstConfirmation) {
      return (
        <InfoRow>
          <InfoText>{t('are_you_sure_you_want_to_delete_your_account')}</InfoText>
        </InfoRow>
      );
    }

    return (
      <InformationPointsSection>
        {informationPoints.map((info, idx) => (
          <InfoRow key={info}>
            <InfoRowIndex>{idx + 1}. </InfoRowIndex>
            <InfoText>{t(info)}</InfoText>
          </InfoRow>
        ))}
      </InformationPointsSection>
    );
  };

  const isSuccess = apiResponse && apiResponse === 'success';

  useEffect(() => {
    return () => {
      if (!isSuccess) {
        return;
      }
      logout();
    };
  }, [isSuccess]);

  const onContinueClickFunc = useCallback(() => {
    if (!apiResponse) {
      if (!firstConfirmation) {
        // First confirmation - move to second confirmation step
        trackEvent('hasContinueDeleteAccount', {identificationProcess});
        setFirstConfirmation(true);
        return;
      }
      // Second confirmation - actually delete the account
      return handleRemoveAccount();
    }
    if (isSuccess && onContinue) {
      return onContinue();
    }
    return onCancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResponse, isSuccess, firstConfirmation]);
  const continueButtonText = (!apiResponse) ? (firstConfirmation ? t('delete_account') : t('continue')) : t('close_credit_modal');
  const showCancelButton = !apiResponse;
  return (
    <Root modal-title="remove_account_modal">
      {isLoading && <CentralLoading />}
      <CloseButton data-id="remove-account-close-button" onClick={onCancel}>
        <ClosePopupButtonSvg alt={t('close')} />
      </CloseButton>
      <DialogHeader withShadow={!apiResponse}>
        <DialogHeaderText id="modal-title">
          {!apiResponse ? capitalFirstLetterAfterSpace(t('delete_account')) : ''}
        </DialogHeaderText>
      </DialogHeader>

      <ModalContent>
        <DeleteAccountContent />
      </ModalContent>

      {isVerified && (
      <ButtonsWrapper>
        {showCancelButton && <CancelButton variant="secondaryXL" onClick={onCancel}>{t('cancel')}</CancelButton>}
        <ContinueButton variant="primaryXL" onClick={onContinueClickFunc} fullWidth={!showCancelButton}>{continueButtonText}</ContinueButton>
      </ButtonsWrapper>
        )}
    </Root>
  );
};

export default memo(DeleteAccountWarning);
